// PublicSubjectForm.jsx
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, LockClosedIcon, UserIcon, CheckIcon } from '@heroicons/react/24/outline';
import { handlePublicSubmission, checkFormPassword, verifyPublicFormPassword, getPublicForm, checkPublicFormAccess, submitPublicForm } from '../../Requests/SubjectRequests';
import { useTranslation } from 'react-i18next';

const PublicSubjectForm = () => {
    const { token } = useParams();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [needsPassword, setNeedsPassword] = useState(false);
    const [password, setPassword] = useState('');
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        property_values: {}
    });
    const [formMetadata, setFormMetadata] = useState({
        title: '',
        description: '',
        propertyDefinitions: []
    });
    const [submitted, setSubmitted] = useState(false);


    useEffect(() => {
        const checkFormAccess = async () => {
            setLoading(true);
            try {
                await checkPublicFormAccess(token);
                const response = await getPublicForm(token);
                setFormMetadata({
                    title: response.data.title,
                    description: response.data.description,
                    propertyDefinitions: response.data.property_definitions || []
                });
                setNeedsPassword(false);
            } catch (error) {
                if (error.response?.status === 403) {
                    setNeedsPassword(true);
                } else {
                    setError(t('PublicForm.FormNotFound'));
                }
            }
            setLoading(false);
        };

        checkFormAccess();
    }, [token]);


    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await verifyPublicFormPassword(token, password);
            if (response.data.valid) {
                const formResponse = await getPublicForm(token);
                setFormMetadata({
                    title: formResponse.data.title,
                    description: formResponse.data.description,
                    propertyDefinitions: formResponse.data.property_definitions || []
                });
                setNeedsPassword(false);
            } else {
                setError(t('PublicForm.InvalidPassword'));
            }
        } catch (error) {
            setError(t('PublicForm.ErrorCheckingPassword'));
        }
        setLoading(false);
    };

    const handleInputChange = (propertyId, value) => {
        setFormData(prev => ({
            ...prev,
            property_values: {
                ...prev.property_values,
                [propertyId]: {
                    property_definition_id: propertyId,
                    value: value
                }
            }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const submitData = {
                ...formData,
                password: needsPassword ? password : undefined,
                property_values: Object.values(formData.property_values)
            };

            await submitPublicForm(token, submitData);
            setSubmitted(true);
        } catch (error) {
            setError(t('PublicForm.SubmissionError'));
        }
        setLoading(false);
    };

    const renderPropertyInput = (property) => {
        switch (property.type) {
            case 'text':
                return (
                    <input
                        type="text"
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm"
                        value={formData.property_values[property.id]?.value || ''}
                        onChange={(e) => handleInputChange(property.id, e.target.value)}
                        required={property.required}
                    />
                );
            case 'number':
                return (
                    <input
                        type="number"
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm"
                        value={formData.property_values[property.id]?.value || ''}
                        onChange={(e) => handleInputChange(property.id, parseFloat(e.target.value))}
                        min={property.min_value}
                        max={property.max_value}
                        required={property.required}
                    />
                );
            case 'date':
                return (
                    <input
                        type="date"
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm"
                        value={formData.property_values[property.id]?.value || ''}
                        onChange={(e) => handleInputChange(property.id, e.target.value)}
                        required={property.required}
                    />
                );
            case 'boolean':
                return (
                    <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-black focus:ring-black"
                        checked={formData.property_values[property.id]?.value || false}
                        onChange={(e) => handleInputChange(property.id, e.target.checked)}
                    />
                );
            case 'select':
                return (
                    <select
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm"
                        value={formData.property_values[property.id]?.value || ''}
                        onChange={(e) => handleInputChange(property.id, e.target.value)}
                        required={property.required}
                    >
                        <option value="">Select an option</option>
                        {property.options?.map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                );
            case 'multiple_select':
                return (
                    <select
                        multiple
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm"
                        value={formData.property_values[property.id]?.value || []}
                        onChange={(e) => handleInputChange(property.id,
                            Array.from(e.target.selectedOptions, option => option.value))}
                        required={property.required}
                    >
                        {property.options?.map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                );
            default:
                return null;
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center p-4">
                <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
                    <div className="text-center">
                        <XMarkIcon className="mx-auto h-12 w-12 text-red-500" />
                        <h2 className="mt-2 text-lg font-medium text-gray-900">{error}</h2>
                    </div>
                </div>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="min-h-screen flex items-center justify-center p-4">
                <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
                    <div className="text-center">
                        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                            <CheckIcon className="h-6 w-6 text-green-600" />
                        </div>
                        <h2 className="mt-2 text-lg font-medium text-gray-900">{t('PublicForm.SubmissionSuccess')}</h2>
                        <p className="mt-2 text-sm text-gray-500">{t('PublicForm.ThankYou')}</p>
                    </div>
                </div>
            </div>
        );
    }

    if (needsPassword) {
        return (
            <div className="min-h-screen flex items-center justify-center p-4">
                <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
                    <div className="text-center mb-6">
                        <LockClosedIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <h2 className="mt-2 text-lg font-medium text-gray-900">{t('PublicForm.PasswordRequired')}</h2>
                        <p className="mt-2 text-sm text-gray-500">{t('PublicForm.EnterPassword')}</p>
                    </div>
                    <form onSubmit={handlePasswordSubmit}>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm"
                            placeholder={t('PublicForm.PasswordPlaceholder')}
                            required
                        />
                        <button
                            type="submit"
                            disabled={loading}
                            className="mt-4 w-full px-4 py-2 text-sm font-medium text-white bg-black rounded-md hover:bg-gray-800 disabled:bg-gray-400"
                        >
                            {loading ? t('Common.Loading') : t('PublicForm.Continue')}
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full my-8">
                {formMetadata.title && (
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">
                        {formMetadata.title}
                    </h1>
                )}
                {formMetadata.description && (
                    <p className="text-gray-600 mb-6">
                        {formMetadata.description}
                    </p>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">{t('PublicForm.Name')}</label>
                        <input
                            type="text"
                            required
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm"
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        />
                    </div>

                    {formMetadata.propertyDefinitions?.map((property) => (
                        <div key={property.id}>
                            <label className="block text-sm font-medium text-gray-700">
                                {property.name}
                                {property.required && <span className="text-red-500">*</span>}
                            </label>
                            <div className="mt-1">
                                {renderPropertyInput(property)}
                            </div>
                        </div>
                    ))}

                    <div className="pt-4">
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full px-4 py-2 text-sm font-medium text-white bg-black rounded-md hover:bg-gray-800 disabled:bg-gray-400"
                        >
                            {loading ? t('Common.Submitting') : t('PublicForm.Submit')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PublicSubjectForm;
